import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { Button, ButtonIcon, Flex, LiveCard, Loading, Page, Spinner, Typography } from 'components';
import useLives from 'features/lives/useLives';
import { LiveMeetingAttendanceConfirmationStatusEnum, LiveMeetingAttendanceConfirmationTypeEnum } from 'app/types/cards/LiveVideoCard';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';
import { useBreakpoints } from 'app/hooks';
import { ConfirmationCode } from 'features/lives/components';
import { AlertState } from 'features/alert/types';

const ConfirmationWrapper = styled(Flex)`
  box-shadow: 0 4px 11px 0 rgba(0, 0, 0, 0.16);
  border-radius: 4px;
`;

const Confirmation = () => {
  const [loading, setLoading] = useState(true);
  const [waiting, setWaiting] = useState(false);

  const [confirmationStatus, setConfirmationStatus] = useState<LiveMeetingAttendanceConfirmationStatusEnum | null>(null);

  const [alert, setAlert] = useState<AlertState>(undefined!);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);

  const { liveUuid } = useParams();
  const [searchParams] = useSearchParams();
  const receivedConfirmationCode = searchParams.get('code');

  const { liveCardDetails, setLiveCardDetails, fetchLive, confirmAttendance } = useLives();
  const breakpoint = useBreakpoints();

  const validConfirmationCode = useMemo(() => confirmationCode?.trim().length === 6, [confirmationCode]);

  const fetchLiveProcess = useCallback(async () => {
    await fetchLive(liveUuid)
      .then((live) => {
        if (!receivedConfirmationCode) setConfirmationStatus(live.liveVideo?.attendanceConfirmationStatus!);
      })
      .finally(() => setLoading(false));
  }, [liveUuid]);

  const confirmation = useCallback(
    async (
      code: string | null,
      type: LiveMeetingAttendanceConfirmationTypeEnum = LiveMeetingAttendanceConfirmationTypeEnum.ClickedOnConfirmationUrl
    ) => {
      if (!code) {
        setAlert({ type: 'error', message: 'Informe o código para confirmar sua presença' });
      } else {
        if (liveUuid) {
          setWaiting(true);

          await confirmAttendance(liveUuid, code, type)
            .then(async () => {
              setConfirmationStatus(LiveMeetingAttendanceConfirmationStatusEnum.Confirmed);
            })
            .catch((error: { apiResultCode: number; apiResultMessage: string }) => {
              setConfirmationCode(null);
              setAlert({ type: 'error', message: error?.apiResultMessage || 'Ocorreu um erro ao confirmar presença' });
              setLoading(false);
              setWaiting(false);
            });
        }
      }
    },
    [liveUuid]
  );

  useEffect(() => {
    fetchLiveProcess();

    if (receivedConfirmationCode) confirmation(receivedConfirmationCode);
  }, [liveUuid]);

  useEffect(() => {
    return () => {
      setLiveCardDetails(null!);
    };
  }, []);

  const iconWidth = breakpoint === 'desktop' ? 44 : 32;

  if (loading) return <Loading label="Por favor, aguarde." />;

  return liveCardDetails ? (
    <Page title="Confirmação de presença">
      <Flex justifyContent="center" alignItems="center">
        <Flex flexGrow={0} flexDirection="column" alignItems="center" style={{ gap: breakpoint === 'desktop' ? 50 : 30 }}>
          <ConfirmationWrapper flexDirection={{ m: 'column', d: 'row' }} width={{ m: 280, d: 'auto' }} flexGrow={0}>
            <LiveCard {...{ ...liveCardDetails, size: breakpoint === 'desktop' ? 'large' : 'default' }} hideButton isConfirmationPage />

            <Flex
              width={{ m: 280, d: 320 }}
              bg="white"
              justifyContent="center"
              alignItems="center"
              borderTopRightRadius={4}
              borderBottomRightRadius={4}
            >
              {confirmationStatus === null ? (
                <>
                  {!alert && <Spinner color="primary_400" />}

                  {alert?.type === 'error' && (
                    <Flex
                      bg="white"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      textAlign="center"
                      p={4}
                      borderBottomLeftRadius={4}
                      borderBottomRightRadius={4}
                    >
                      <ButtonIcon icon="accountCancel" bg="transparent" color="alert_400" cursor="inherit" iconWidth={iconWidth} />

                      <Typography mt={3} fontWeight="bold">
                        {alert?.message}
                      </Typography>
                    </Flex>
                  )}
                </>
              ) : (
                <>
                  {confirmationStatus === LiveMeetingAttendanceConfirmationStatusEnum.Confirmed && (
                    <Flex
                      bg="white"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      p={4}
                      borderBottomLeftRadius={4}
                      borderBottomRightRadius={4}
                    >
                      <ButtonIcon icon="accountCheck" bg="transparent" color="success_400" cursor="inherit" iconWidth={iconWidth} />

                      <Typography mt={3} fontWeight="bold">
                        Sua presença foi confirmada
                      </Typography>

                      {!!liveCardDetails?.confirmationAt && (
                        <Typography mt={1} type="type0" color="black50">
                          em {formatDateFromUtcToTimezone(liveCardDetails?.confirmationAt).format('DD/MM/YYYY hh:mm')}
                        </Typography>
                      )}
                    </Flex>
                  )}

                  {confirmationStatus === LiveMeetingAttendanceConfirmationStatusEnum.Expired && (
                    <Flex
                      bg="white"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      p={4}
                      borderBottomLeftRadius={4}
                      borderBottomRightRadius={4}
                    >
                      <ButtonIcon icon="accountCancel" bg="transparent" color="alert_400" cursor="inherit" iconWidth={iconWidth} />

                      <Typography mt={3} fontWeight="bold">
                        Confirmação de presença expirada
                      </Typography>

                      {!!liveCardDetails?.confirmationCodeExpiresAt && (
                        <Typography mt={1} type="type0" color="black50">
                          em {formatDateFromUtcToTimezone(liveCardDetails?.confirmationCodeExpiresAt).format('DD/MM/YYYY hh:mm')}
                        </Typography>
                      )}
                    </Flex>
                  )}

                  {confirmationStatus === LiveMeetingAttendanceConfirmationStatusEnum.Pending && (
                    <Flex position="relative">
                      <Flex
                        flexDirection="column"
                        justifyItems="flex-start"
                        width={320}
                        bg="white"
                        p={3}
                        borderBottomLeftRadius={4}
                        borderBottomRightRadius={4}
                        position="relative"
                        zIndex={4}
                        style={{
                          transitionDelay: '0s',
                          transitionProperty: 'margin,opacity',
                          transitionTimingFunction: 'ease-in-out',
                          transitionDuration: '0.5s',
                        }}
                      >
                        <Typography type="type1" fontWeight="bold" textAlign="center" mb={3}>
                          Informe o código apresentado durante o evento ao-vivo:
                        </Typography>

                        <ConfirmationCode hasError={!!alert?.message} confirmationCode={confirmationCode} setConfirmationCode={setConfirmationCode} />

                        {alert?.type === 'error' && (
                          <Typography type="type1" color="alert_400" textAlign="center" mt={2}>
                            {alert?.message}
                          </Typography>
                        )}
                        {alert?.type === 'success' && (
                          <Typography type="type1" color="success_400" textAlign="center" mt={2}>
                            {alert?.message}
                          </Typography>
                        )}

                        <Button
                          mt={3}
                          flex={1}
                          bg={!validConfirmationCode ? 'gray_500' : 'primary_400'}
                          bgHover={!validConfirmationCode ? 'gray_500' : 'primary_200'}
                          color={!validConfirmationCode ? 'black80' : 'white'}
                          colorHover={!validConfirmationCode ? 'black80' : 'white'}
                          disabled={!validConfirmationCode}
                          flexShrink={0}
                          p={2}
                          onClick={() => confirmation(confirmationCode, LiveMeetingAttendanceConfirmationTypeEnum.InputConfirmationCode)}
                        >
                          Confirmar presença {waiting && <></>}
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </ConfirmationWrapper>

          <Link to="/">
            <Button>Ir para a home</Button>
          </Link>
        </Flex>
      </Flex>
    </Page>
  ) : (
    <></>
  );
};

export default Confirmation;
